/**
 * Semantic tone helpers for the Designer Dashboard module.
 */

import type { StatusBadgeVariant } from "@/lib/orderStatusHelpers";
import { statusDotClass, statusSurfaceClass } from "@/lib/orderStatusHelpers";

/** Waiting-timer text color by urgency bucket. */
export const WAITING_TIME_TEXT: Record<
  Extract<StatusBadgeVariant, "success" | "warning" | "destructive">,
  string
> = {
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
};

/** Waiting-timer left rail — solid semantic dot colors. */
export const waitingTimeRail = (
  urgency: Extract<StatusBadgeVariant, "success" | "warning" | "destructive">,
): string => statusDotClass(urgency);

/** New design vs revision chips, strips, and card left rails. */
export const DESIGN_TYPE = {
  new: {
    chip: "bg-success-background text-success border-success-border",
    strip: "bg-emerald-500",
    rail: "bg-emerald-500",
  },
  revision: {
    chip: "bg-warning-background text-warning border-warning-border",
    strip: "bg-amber-500",
    rail: "bg-amber-500",
  },
} as const;

/** Activity timeline marker dots. */
export const ACTIVITY_DOT: Record<string, string> = {
  claim: "bg-success",
  unclaim: "bg-warning",
  revision_requested: "bg-info",
  upload: "bg-success",
  design_finished: "bg-success",
  status_change: "bg-primary",
  create: "bg-primary",
};

export const activityDotClass = (actionType: string): string =>
  ACTIVITY_DOT[actionType] ?? "bg-muted-foreground";

/** Activity timeline badge surfaces. */
export const ACTIVITY_BADGE: Record<string, string> = {
  claim: statusSurfaceClass("success"),
  unclaim: statusSurfaceClass("warning"),
  revision_requested: statusSurfaceClass("info"),
  upload: statusSurfaceClass("success"),
  design_finished: statusSurfaceClass("success"),
  status_change: statusSurfaceClass("indigo"),
  create: "bg-primary/10 text-primary border border-primary/20",
};

export const activityBadgeClass = (actionType: string): string =>
  ACTIVITY_BADGE[actionType] ?? statusSurfaceClass("secondary");

/** Upload / revision status badges on completed-design tabs. */
export const UPLOAD_BADGE = {
  uploaded: "bg-success text-success-foreground hover:bg-success",
  pending: "bg-warning text-warning-foreground hover:bg-warning",
  revision: "bg-pastel-lavender text-pastel-lavender-fg hover:bg-pastel-lavender",
  current: "bg-primary text-primary-foreground hover:bg-primary",
} as const;

/** Request submitter role chips — see `@/lib/teamRolePresentation`. */
export const ROLE_ORIGIN_CHIP: Record<string, string> = {
  client: "bg-info-background text-info",
  admin: "bg-primary/10 text-primary",
  sales: "bg-success-background text-success",
  dsa: "bg-info-background text-info",
  ops: "bg-muted text-muted-foreground",
  licensing_ops: "bg-primary/10 text-primary",
  designer: "bg-info-background text-info",
  intern: "bg-warning-background text-warning",
  internal: "bg-muted text-muted-foreground",
};

/** Design type filter dots. */
export const DESIGN_FILTER_DOT = {
  new: "bg-success",
  revision: "bg-warning",
} as const;

/** Client revision note highlight. */
export const REVISION_NOTE_HIGHLIGHT =
  "p-3 bg-info-background rounded-lg border border-info-border";
